import { useParams } from 'react-router-dom';
import { useSysAdminUserGet } from '../hooks/use-sysadmin-user.hook';
import { memo, useCallback, useMemo } from 'react';
import SysAdminUserDetailsComponent from '../components/sysadmin-user-details.component';
import { DetailItem } from '../../../../common/RenderDetails';
import { SysAdminUser } from '../types/sysadmin-user.types';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { formatDate } from '@utils/date.util';
import { Avatar, Tag } from 'antd';
import { WithSysAdminPermissionCheck } from '../../../../auth/WithSysAdminPermissionCheck';
import PageTitle from '@components/common/PageTitle';

const SysAdminUserDetailsContainer = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { data, isLoading, error } = useSysAdminUserGet(id || '');

  const prepareDetailsData = useCallback(
    (dataObj: SysAdminUser | undefined): DetailItem[] => {
      if (!dataObj) return [];

      const detailItems: DetailItem[] = [];
      // prepare data to display in the details card
      Object.entries(dataObj).forEach(([key, value]) => {
        if (key === 'createdAt' || key === 'updatedAt') {
          detailItems.push({
            label: t(`common.${key}.label`),
            value: formatDate(value),
          });
          return;
        }
        if (key === 'id') {
          detailItems.push({
            label: t(`common.${key}.label`),
            value: value.toString(),
          });
          return;
        }
        if (key === 'role') {
          detailItems.push({
            label: t(`users.${key}.label`),
            value: value.name,
          });
          return;
        }
        if (key === 'isSysAdmin') {
          detailItems.push({
            label: t(`users.${key}.label`),
            value: value ? (
              <Tag color="green">{t('common.yes')}</Tag>
            ) : (
              <Tag color="red">{t('common.no')}</Tag>
            ),
          });
          return;
        }
        if (key === 'lastLoginAt') {
          detailItems.push({
            label: t(`users.${key}.label`),
            value: formatDate(value),
          });
          return;
        }
        if (key === 'emailVerified') {
          detailItems.push({
            label: t(`users.${key}.label`),
            value: value ? (
              <Tag color="green">{t('common.yes')}</Tag>
            ) : (
              <Tag color="red">{t('common.no')}</Tag>
            ),
          });
          return;
        }

        if (key === 'picture') {
          detailItems.push({
            label: t(`users.${key}.label`),
            value: (
              <Avatar src={value} alt={t('users.picture.label')}>
                {!value && dataObj.name[0]}
              </Avatar>
            ),
          });
          return;
        }

        detailItems.push({
          label: t(`users.${key}.label`),
          value: String(value),
        });
      });

      return detailItems;
    },
    [t]
  );

  if (!id)
    return (
      <ErrorFallback
        details={t('crud.details.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.details.title', { entity: t('users.title') })} />
        <HeaderRow
          title={t('crud.details.title', { entity: t('users.title') })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('common.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  const detailItems = prepareDetailsData(data?.data);

  return (
    <>
      <PageTitle title={t('crud.details.title', { entity: t('users.title') })} />
      <HeaderRow
        title={t('crud.details.title', { entity: t('users.title') })}
        showBackButton={true}
        trailTarget={id}
        isSysAdmin={true}
      />
      <div className="row mt-3">
        <SysAdminUserDetailsComponent
          data={detailItems}
          success={data?.success ?? false}
          isLoading={isLoading}
          error={error}
        />
      </div>
    </>
  );
};

export default WithSysAdminPermissionCheck(memo(SysAdminUserDetailsContainer), 'user.view');
